import { formatCropYear, type MapField } from '@/features/fields';

import type { DeviceLocation } from './details-schemas';

/**
 * Fold the joined Field into the device's own location.
 *
 * `GET /devices/tracking/{id}` returns `location` as **only** `{latitude, longitude}` — no field,
 * crop, place or acreage (probed 2026-09-01; see `use-linked-field.ts` for the full endpoint
 * table). So every value below the map on the Device Location card comes from `GET /fields/tracking`
 * instead, and this is where the two halves meet: the device keeps its own coordinates, the Field
 * supplies the context drawn around them.
 *
 * A device that sits in no Field is returned untouched — the card's "located, but outside any
 * field" state is just the pin, with no name/acres row, and that is a real state rather than a
 * failure.
 */
export function withLinkedField(
  location: DeviceLocation | null,
  field: MapField | null,
): DeviceLocation | null {
  if (!location) return null;
  if (!field) return location;

  return {
    ...location,
    fieldId: field.id,
    fieldName: field.name,
    city: field.city,
    state: field.state,
    acres: field.acreage,
    cropName: field.crop,
    // The card renders the season on its own line, so it is resolved to its display form here
    // rather than passed as the `{ start, end }` pair the fields API sends.
    cropYear: field.cropYear ? formatCropYear(field.cropYear) : null,
    // `lon` on the fields wire, `lng` in the Maps API. Renamed once, at this boundary.
    boundary: field.boundary.map((ring) => ring.map(({ lat, lon }) => ({ lat, lng: lon }))),
  };
}
